import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Pressable,
  ScrollView,
  View,
} from "react-native"
import { router, useLocalSearchParams } from "expo-router"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import FullScreenBackground from "@/components/backgrounds/FullScreenBackgroundGradient"
import { H2 } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import {
  LoanDashboardLoanListModal,
  LoanDashboardLoanListModalProvider,
  useLoanDashboardLoanListModal,
} from "../LoanDashboardLoanListModal"
import { useCallback, useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { RefreshControl } from "react-native-gesture-handler"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { useOnRefocusEffect } from "@/hooks/useOnReFocusEffect"
import { ChevronDown } from "@/lib/icons/ChevronDown"

function LayoutHeader() {
  const insets = useSafeAreaInsets()
  const { onShow } = useLoanDashboardLoanListModal()

  return (
    <View style={{ paddingTop: 16 + insets.top }} className={"px-5 pb-1 gap-3"}>
      <Horizontal className="items-center justify-start h-16 gap-5">
        <Pressable
          onPress={onShow}
          testID="my-loans"
          className={"flex-1 gap-2 flex-row"}
        >
          <H2 className={"text-white"}>My Loans</H2>
          <ChevronDown color={"white"} />
        </Pressable>
        <Vertical className="items-center justify-center">
          <PrimaryDarkButton
            testID="new-quote"
            title="New Quote +"
            size="sm"
            className="w-[100px]"
            onPress={() => {
              router.navigate("/loans/landing")
            }}
          />
        </Vertical>
      </Horizontal>
    </View>
  )
}

export default function LoanDashboardLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const insets = useSafeAreaInsets()
  const { isWeb } = useDeviceAttributes()
  const scrollRef = useRef<ScrollView>(null)
  const { loanId } = useLocalSearchParams<{ loanId?: string }>()

  const [showRefreshing, setShowRefreshing] = useState(false)
  const [userInitiatedRefresh, setUserInitiatedRefresh] = useState(false)

  useOnRefocusEffect(() => {
    // Scroll to top on navigation to the dashboard screen.
    scrollRef.current?.scrollTo({ x: 0, y: 0, animated: true })
  })

  const query = useQuery(loanDashboardQueryOptions(loanId))

  useEffect(() => {
    if (!query.isRefetching) {
      // when refreshing is done, reset the user initiated refresh state
      setUserInitiatedRefresh(false)
    }
  }, [query.isRefetching])

  const onRefreshMinTimeFinished = useCallback(() => {
    setShowRefreshing(false)
  }, [])

  const onRefresh = useCallback(() => {
    if (loanId && !query.isRefetching) {
      setShowRefreshing(true)
      setUserInitiatedRefresh(true)
      // Always show the refresh indicator for at least 1 second
      // so it feels like its doing something.
      setTimeout(onRefreshMinTimeFinished, 1000)
      void query.refetch()
    }
  }, [loanId, query.isRefetching])

  return (
    <LoanDashboardLoanListModalProvider>
      <KeyboardAvoidingView
        testID="LoanDashboardScreen"
        className="flex flex-col flex-1"
        behavior="padding"
      >
        <FullScreenBackground />
        <LayoutHeader />
        <Vertical className="flex-1 bg-off-white rounded-t-[20px] overflow-hidden">
          <ScrollView
            className="flex-1 bg-off-white"
            contentContainerStyle={{
              paddingBottom: insets.bottom,
            }}
            ref={scrollRef}
            showsVerticalScrollIndicator={false}
            refreshControl={
              !isWeb ? (
                <RefreshControl
                  refreshing={
                    showRefreshing ||
                    (userInitiatedRefresh && query.isRefetching)
                  }
                  onRefresh={onRefresh}
                />
              ) : undefined
            }
          >
            <Vertical className="flex-1 gap-5 p-5">
              {query.isLoading ? <ActivityIndicator /> : children}
            </Vertical>
          </ScrollView>
        </Vertical>
      </KeyboardAvoidingView>
      <LoanDashboardLoanListModal />
    </LoanDashboardLoanListModalProvider>
  )
}
